import React, { useMemo } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { TabList } from '@/components/ui/navigation';
import { PrimaryButton } from '@/components/ui/buttons';
import { IconScout } from '@/components/ui/images/IconScout';
import { LocalIcon } from '@/components/ui/images/LocalIcon';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { ActionMenu, type ActionMenuItem } from '@/components/ui/menus';
import { PageSpinner, Skeleton } from '@/components/ui/loading';
import { Text } from '@/components/ui/text/Text';
import {
  AttendanceSituationCard,
  AttendanceSituationCardSkeleton,
  TodayMarksCard,
  WeeklyAttendanceCard,
  MonthSummarySection,
  MyTeamTabContent,
  CompanyTabContent,
} from '@/components/features/attendance/dashboard';
import { useTranslation } from '@/i18n';
import { useAttendanceDashboard } from '@/hooks/attendance/dashboard/useAttendanceDashboard';
import type { DashboardTab } from '@/hooks/attendance/dashboard/useAttendanceDashboard';
import type {
  AttendanceDashboard as AttendanceDashboardData,
  DashboardMark,
  DashboardMonthly,
  DashboardWeekly,
} from '@/api/domains/attendance';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { colors } from '@/assets/styles/colors';
import type { UIExportFormat } from '@/types/fileExport.types';


// Permiso independiente del tab "Empresa" (ASIS-5638, code review finding 4): no
// reutiliza DASHBOARD_ASISTENCIA (el permiso genérico de la página).
//
// IMPORTANTE — este NO es el WEB_INTERFACE del backend ("DASHBOARD_EMPRESA", ver
// CompanyDashboardSections.cs). Ese string solo se usa server-side para resolver
// v2_pfMenuItem.InterfazWeb en el `[RequireEmployeePermission]` de los 3 endpoints de
// filtros — es un mecanismo distinto del que usa el frontend para pintar/ocultar el tab.
//
// El Redux permissionsMap que alimenta usePagePermissions se construye en
// extractPermissionsFromMenu (src/api/domains/menu/menu.mapper.ts) a partir de GET /menu,
// e indexa cada pantalla por `code` — es decir, v2_pfMenuItem.Codigo (el mismo campo que
// ROUTE_DEFINITIONS.<X>.code, ej. PAYMENT_CONCEPTS.code usado en useMonthlyCutsList.ts),
// NUNCA por InterfazWeb. La migración que siembra el permiso
// (db/liquibase/BnovusCorreos/changelog/migrations/V034__seed_dashboard_empresa_permission.sql)
// inserta ese menu item con Codigo = '1.5.4' — de ahí este valor. No se agrega como
// entrada propia en ROUTE_DEFINITIONS porque el tab no navega a una pantalla/path
// independiente (vive dentro de la misma URL que Dashboard Asistencia); se resuelve
// pasando el código explícitamente a usePagePermissions, igual que useMonthlyCutsList.ts
// hace con ROUTE_DEFINITIONS.PAYMENT_CONCEPTS.code.
const DASHBOARD_EMPRESA_SCREEN_CODE = '1.5.4';

interface MiAsistenciaTabContentProps {
  dashboard: AttendanceDashboardData | null;
  weeklyData: DashboardWeekly | null;
  monthly: DashboardMonthly | null;
  todayMarks: DashboardMark[];
  hasMarksError: boolean;
  isLoadingMain: boolean;
  isLoadingMarks: boolean;
  isLoadingMonthly: boolean;
  isLoadingWeekly: boolean;
  isLoadingCutoffs: boolean;
  situationDateLabel: string;
  selectedYear: string;
  selectedMonth: string;
  selectedCutId: string;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  cutOptions: { value: string; label: string }[];
  isCutSelectorDisabled: boolean;
  setSelectedYear: (year: string) => void;
  setSelectedMonth: (month: string) => void;
  setSelectedCutId: (cutId: string) => void;
  selectedCompanyId: string;
  setSelectedCompanyId: (companyId: string) => void;
  companyOptions: { value: string; label: string }[];
  onExportDashboard: (format: UIExportFormat) => Promise<void>;
  isExportingDashboard: boolean;
  onRegisterAttendance: () => void;
  isTodayMarkedAbsent: boolean;
  onRegisterIncidentFromWeek: () => void;
  onViewTodayMarksDetail: () => void;
}

const MiAsistenciaTabContent: React.FC<MiAsistenciaTabContentProps> = ({
  dashboard,
  weeklyData,
  monthly,
  todayMarks,
  hasMarksError,
  isLoadingMain,
  isLoadingMarks,
  isLoadingMonthly,
  isLoadingWeekly,
  isLoadingCutoffs,
  situationDateLabel,
  selectedYear,
  selectedMonth,
  selectedCutId,
  yearOptions,
  monthOptions,
  cutOptions,
  isCutSelectorDisabled,
  setSelectedYear,
  setSelectedMonth,
  setSelectedCutId,
  selectedCompanyId,
  setSelectedCompanyId,
  companyOptions,
  onExportDashboard,
  isExportingDashboard,
  onRegisterAttendance,
  isTodayMarkedAbsent,
  onRegisterIncidentFromWeek,
  onViewTodayMarksDetail,
}) => {
  const { t: translate } = useTranslation();

  const situationMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'registerAttendance',
      label: translate('attendance:create'),
      icon: 'clockThree',
      color: colors.important.main,
      onClick: onRegisterAttendance,
    },
  ], [translate, onRegisterAttendance]);

  const todayMarksMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.marks.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: onViewTodayMarksDetail,
    },
  ], [translate, onViewTodayMarksDetail]);

  const weeklyMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'registerIncident',
      label: translate('attendance:dashboard.weeklyCard.menu.registerIncident'),
      icon: 'clockThree',
      color: colors.important.main,
      onClick: onRegisterIncidentFromWeek,
    },
  ], [translate, onRegisterIncidentFromWeek]);

  const scheduleTheoretical = useMemo(() => {
    if (!dashboard) return '';
    const {
      isFreeSchedule, hasSchedule, scheduleEntry, scheduleExit, lunchBreak,
    } = dashboard.situation;
    if (isFreeSchedule) return translate('attendance:dashboard.schedule.free');
    if (!hasSchedule) return translate('attendance:dashboard.schedule.noSchedule');
    if (lunchBreak) return translate('attendance:dashboard.schedule.theoreticalWithLunch', { entry: scheduleEntry, exit: scheduleExit, lunch: lunchBreak });
    return translate('attendance:dashboard.schedule.theoretical', { entry: scheduleEntry, exit: scheduleExit });
  }, [dashboard, translate]);

  const scheduleReal = useMemo(() => {
    if (!dashboard) return '';
    const { marksEntry, marksExit } = dashboard.situation;
    if (marksEntry && marksExit) return translate('attendance:dashboard.schedule.realWithExit', { entry: marksEntry, exit: marksExit });
    if (marksEntry) return translate('attendance:dashboard.schedule.realNoExit', { entry: marksEntry });
    return translate('attendance:dashboard.marks.noMarks');
  }, [dashboard, translate]);

  const translatedMetrics = useMemo(
    () => dashboard?.situation.metrics.map((m) => ({
      label: translate(m.labelKey),
      value: m.value,
    })) ?? [],
    [dashboard, translate],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text
          variant="body-medium"
          fontFamily="montserrat"
          color={colors.grays.neutral66}
          style={{ fontWeight: 600 }}
          data-testid="situation-section-title"
        >
          {translate('attendance:dashboard.situationTitle')}{' '}
          <span style={{ fontWeight: 400 }}>{situationDateLabel}</span>
        </Text>
        <PermissionGuard action="view" fallback="hidden">
          <ExportDropdown
            onExport={(format) => void onExportDashboard(format)}
            disabled={!dashboard}
            isLoading={isExportingDashboard}
            placement="right"
            availableFormats={['PDF', 'EXCEL']}
            data-testid="situation-download-button"
          />
        </PermissionGuard>
      </div>

      {(isLoadingMain || !dashboard) ? (
        <AttendanceSituationCardSkeleton data-testid="situation-card-skeleton" />
      ) : (
        <AttendanceSituationCard
          status={dashboard.situation.status}
          statusLabel={dashboard.situation.statusLabel}
          scheduleTheoretical={scheduleTheoretical}
          scheduleReal={scheduleReal}
          metrics={translatedMetrics}
          headerAction={(
            <PermissionGuard action="create" fallback="hidden">
              <ActionMenu
                items={situationMenuItems}
                triggerIcon="moreVertical"
                placement="right"
                offsetX={-60}
                ariaLabel={translate('attendance:create')}
              />
            </PermissionGuard>
          )}
          data-testid="situation-card"
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <TodayMarksCard
          marks={todayMarks}
          isLoading={isLoadingMarks}
          hasError={hasMarksError}
          headerAction={(
            <PermissionGuard action="view" fallback="hidden">
              <ActionMenu
                items={todayMarksMenuItems}
                triggerIcon="moreVertical"
                placement="right"
                ariaLabel={translate('attendance:dashboard.marks.viewDetail')}
              />
            </PermissionGuard>
          )}
          data-testid="today-marks-card"
        />
        {isLoadingWeekly || !weeklyData
          ? <Skeleton height={200} data-testid="weekly-card-skeleton" />
          : (
            <WeeklyAttendanceCard
              days={weeklyData.days}
              workedHours={weeklyData.workedHours}
              totalHours={weeklyData.totalHours}
              headerAction={isTodayMarkedAbsent && (
                <PermissionGuard action="create" fallback="hidden">
                  <ActionMenu
                    items={weeklyMenuItems}
                    triggerIcon="moreVertical"
                    placement="right"
                    offsetX={-50}
                    ariaLabel={translate('attendance:dashboard.weeklyCard.menu.ariaLabel')}
                  />
                </PermissionGuard>
              )}
              data-testid="weekly-card"
            />
          )
        }
      </div>

      {(isLoadingCutoffs || selectedYear !== '') && (
        <MonthSummarySection
          data={monthly}
          isLoadingData={isLoadingMonthly}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedCutId={selectedCutId}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          cutOptions={cutOptions}
          isCutSelectorDisabled={isCutSelectorDisabled}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          onCutChange={setSelectedCutId}
          isLoadingFilters={isLoadingCutoffs}
          selectedCompanyId={selectedCompanyId}
          onCompanyChange={setSelectedCompanyId}
          companyOptions={companyOptions}
          data-testid="month-summary-section"
        />
      )}
    </div>
  );
};

export const AttendanceDashboard: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    employeeFirstName,
    activeTab,
    setActiveTab,
    dashboard,
    weeklyData,
    monthly,
    todayMarks,
    hasMarksError,
    isLoadingMain,
    isLoadingMarks,
    isLoadingMonthly,
    isLoadingWeekly,
    isLoadingCutoffs,
    lastUpdate,
    situationDateLabel,
    selectedYear,
    selectedMonth,
    selectedCutId,
    lastOpenCutYear,
    lastOpenCutMonth,
    yearOptions,
    monthOptions,
    cutOptions,
    isCutSelectorDisabled,
    setSelectedYear,
    setSelectedMonth,
    setSelectedCutId,
    selectedCompanyId,
    setSelectedCompanyId,
    companyOptions,
    refresh,
    openRegisterAttendance,
    isTodayMarkedAbsent,
    onRegisterIncidentFromWeek,
    onViewTodayMarksDetail,
    canManualRefresh,
    isActiveTabReady,
    registerMyTeamLoading,
    onExportDashboard,
    isExportingDashboard,
  } = useAttendanceDashboard();
  const { canView, isLoaded } = usePagePermissions();
  // Gate del tab "Empresa" por su propio permiso (finding 4): sin lectura en
  // DASHBOARD_EMPRESA el tab no se agrega al TabList, por lo que CompanyTabContent
  // nunca se monta y sus 3 llamadas a los endpoints de filtros no se disparan.
  const { canView: canViewCompanyDashboard } = usePagePermissions(DASHBOARD_EMPRESA_SCREEN_CODE);

  const tabs = !canView ? [] : [
    {
      id: 'mi-asistencia' as DashboardTab,
      label: translate('attendance:dashboard.tabs.myAttendance'),
      content: (
        <MiAsistenciaTabContent
          dashboard={dashboard}
          weeklyData={weeklyData}
          monthly={monthly}
          todayMarks={todayMarks}
          hasMarksError={hasMarksError}
          isLoadingMain={isLoadingMain}
          isLoadingMarks={isLoadingMarks}
          isLoadingMonthly={isLoadingMonthly}
          isLoadingWeekly={isLoadingWeekly}
          isLoadingCutoffs={isLoadingCutoffs}
          situationDateLabel={situationDateLabel}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedCutId={selectedCutId}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          cutOptions={cutOptions}
          isCutSelectorDisabled={isCutSelectorDisabled}
          setSelectedYear={setSelectedYear}
          setSelectedMonth={setSelectedMonth}
          setSelectedCutId={setSelectedCutId}
          selectedCompanyId={selectedCompanyId}
          setSelectedCompanyId={setSelectedCompanyId}
          companyOptions={companyOptions}
          onExportDashboard={onExportDashboard}
          isExportingDashboard={isExportingDashboard}
          onRegisterAttendance={openRegisterAttendance}
          isTodayMarkedAbsent={isTodayMarkedAbsent}
          onRegisterIncidentFromWeek={onRegisterIncidentFromWeek}
          onViewTodayMarksDetail={onViewTodayMarksDetail}
        />
      ),
    },
    {
      id: 'mi-equipo' as DashboardTab,
      label: translate('attendance:dashboard.tabs.myTeam'),
      content: (
        <MyTeamTabContent onLoadingChange={registerMyTeamLoading} />
      ),
    },
    ...(canViewCompanyDashboard ? [{
      id: 'empresa' as DashboardTab,
      label: translate('attendance:dashboard.tabs.company'),
      content: (
        <CompanyTabContent />
      ),
    }] : []),
  ];

  return (
    <PageLayout hideBreadcrumb hideCard>
      <div
        style={{ display: 'flex', flexDirection: 'column' }}
        data-testid="attendance-dashboard-page"
      >
        {!isLoaded ? (
          <PageSpinner data-testid="dashboard-permissions-spinner" />
        ) : tabs.length === 0 ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', padding: '48px 0' }}
            data-testid="dashboard-no-access"
          >
            <LocalIcon name="accessDenied" className="w-64 h-auto" />
            <Text variant="body-medium" fontFamily="montserrat" style={{ fontWeight: 600 }}>
              {translate('attendance:dashboard.noAccess.title')}
            </Text>
            <Text variant="body-regular" color={colors.grays.neutral66}>
              {translate('attendance:dashboard.noAccess.description')}
            </Text>
          </div>
        ) : (
          <>
            <div
              className="-mx-4 px-4 md:-mx-8 md:px-8 lg:-mx-16 lg:px-16"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                height: 150,
                paddingTop: 32,
                backgroundColor: colors.grays.neutralFF,
                border: 'none',
              }}
              data-testid="dashboard-header-section"
            >
              {employeeFirstName && (
                <Text
                  variant="body-medium"
                  fontFamily="montserrat"
                  color={colors.primary.dark}
                  style={{ fontSize: 25, fontWeight: 600 }}
                  data-testid="dashboard-greeting"
                >
                  {translate('attendance:dashboard.greeting', { name: employeeFirstName })}
                </Text>
              )}
              {isLoadingCutoffs ? (
                <Skeleton width={280} height={20} />
              ) : (lastOpenCutYear && lastOpenCutMonth) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Text
                    variant="body-regular"
                    fontFamily="montserrat"
                    color={colors.primary.dash}
                    style={{ fontSize: 16, fontWeight: 400 }}
                    data-testid="dashboard-last-open-cutoff-label"
                  >
                    {translate('attendance:dashboard.lastOpenCutoff')}
                  </Text>
                  <Text
                    variant="body-medium"
                    fontFamily="montserrat"
                    color={colors.primary.dash}
                    style={{ fontSize: 16, fontWeight: 600 }}
                    data-testid="dashboard-last-open-cutoff-value"
                  >
                    {`${translate(`attendance:dashboard.months.${parseInt(lastOpenCutMonth, 10) - 1}`)}-${lastOpenCutYear}`}
                  </Text>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, paddingTop: 20 }}>
                <PermissionGuard action="edit">
                  <PrimaryButton
                    variant="outline"
                    onClick={refresh}
                    iconRight={<IconScout name="sync" />}
                    disabled={!canManualRefresh || !isActiveTabReady}
                    data-testid="dashboard-refresh-button"
                  >
                    {translate('attendance:dashboard.refresh')}
                  </PrimaryButton>
                </PermissionGuard>
                {lastUpdate && (
                  <Text
                    variant="small-regular"
                    color={colors.grays.neutral66}
                    data-testid="dashboard-last-update"
                  >
                    {translate('attendance:dashboard.lastUpdate', { datetime: lastUpdate })}
                  </Text>
                )}
              </div>

              <TabList
                tabs={tabs}
                defaultActiveTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as DashboardTab)}
                data-testid="dashboard-tab-list"
              />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AttendanceDashboard;
